import { useState, useEffect, useCallback, useMemo } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import type {
  YardInspection,
  YardInspectionEvidence,
  UpdateYardInspectionParams,
  UpdateYardInspectionRegistrationParams,
} from "../../../store/oasis/yardInspectionsStore";
import {
  deriveInspectionStatus,
  manualExitMissing,
  inspectionStatusLabel,
  inspectionStatusClasses,
  inspectionStatusIcon,
  fetchInspectionGeofenceEvent,
  MISSING_EXIT_AFTER_HOURS,
  WATER_PARAMETERS,
  waterParameterWarning,
  type InspectionGeofenceEvent,
  type WaterParameterKey,
} from "../../../store/oasis/yardInspectionsStore";
import {
  fetchYardAccessEvents,
  fetchYardAccessEvent,
  type YardAccessEvent,
} from "../../../store/oasis/hikvisionAccessStore";
import type { TruckYardVehicle } from "../../../store/truckYardStore";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import {
  WATER_TYPES,
  effectiveWaterTypes,
} from "../../patios-maniobras/components/waterTypes";
import { useGroupsStore } from "../../../store/admin/groupsStore";
import { useAllGroups } from "../../../features/catalogs/queries";
import { brand } from "../../../config/theme";
import CameraCaptureModal from "./CameraCaptureModal";
import { modalYardName, toYardMillis, visitJourney } from "../inspectionTimes";
import { notificationDeepLinkPath } from "../../../features/notifications/notificationDeepLink";
import { canEnterAdminApp } from "../../../router/adminAppAccess";
import { useUserStore } from "../../../store/userStore";
import {
  canOpenUntaggedStep,
  specialInspectionKind,
  untaggedInitialStep,
  untaggedRegistrationComplete,
  exitTagSurveyMode,
  exitTagSurveyComplete,
  exitTagSurveyPayload,
  type ExitTagSurveyMode,
  type TagSurvey,
  type UntaggedStep,
} from "../../../store/oasis/specialInspection";
import SpecialTagBadge from "../../../components/inspections/SpecialTagBadge";
import ProviderVisitModal from "./ProviderVisitModal";
import {
  UntaggedRegistrationStep,
  UntaggedStepIndicator,
  TagSurveyFields,
} from "./UntaggedInspectionSteps";

// ── Photo slots ──────────────────────────────────────────────────────────────
// Three named evidence slots for water-quality inspections. Each slot owns
// the evidence whose `title` matches its `label` (case-insensitive). Any
// extra evidence — uploaded outside of a slot — appears in the "Otras
// fotografías" grid below.
// El slot sale entero de WATER_PARAMETERS (que a su vez toma los títulos de
// REQUIRED_PHOTO_TITLES), así que la foto que pide `deriveInspectionStatus`, el
// número que se persiste y el hueco que ve el operador son la misma cosa y no
// pueden separarse. Sólo el icono vive acá.
//
// Indexado por `key` y no por posición a propósito: con un `string[]` posicional
// y `noUncheckedIndexedAccess` apagado, agregar un cuarto parámetro compilaba
// limpio y renderizaba className="undefined text-primary-500 text-base" — un
// icono en blanco, sin error en ningún lado. Un Record sobre WaterParameterKey
// no compila hasta que el icono nuevo existe.
const SLOT_ICONS: Record<WaterParameterKey, string> = {
  ph: "ri-test-tube-line",
  conductividad: "ri-flashlight-line",
  turbidez: "ri-drop-line",
};
export const PHOTO_SLOTS = WATER_PARAMETERS.map((param) => ({
  key: param.key,
  label: param.photoTitle,
  icon: SLOT_ICONS[param.key],
  param,
}));
export type PhotoSlotKey = (typeof PHOTO_SLOTS)[number]["key"];

const slotLabels = new Set(PHOTO_SLOTS.map((s) => s.label.toLowerCase()));
const findSlotEvidence = (
  evidences: YardInspectionEvidence[],
  slotLabel: string,
) =>
  evidences.find(
    (e) => (e.title || "").trim().toLowerCase() === slotLabel.toLowerCase(),
  ) || null;
const isExtraEvidence = (ev: YardInspectionEvidence) =>
  !slotLabels.has((ev.title || "").trim().toLowerCase());

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatYardTs(ts: unknown): string {
  const millis = toYardMillis(ts);
  if (!millis) return "—";
  return new Date(millis).toLocaleString("es-MX", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// ── Sección colapsable ───────────────────────────────────────────────────────
// Lo de sólo lectura de arriba (eventos de acceso, geocerca) ocupaba la primera
// pantalla entera del modal —en un teléfono, más de una— y el inspector tenía
// que pasarlo en CADA inspección para llegar a lo único que captura él:
// "Completar inspección" y las fotos. Es UN grupo y arranca cerrado.
//
// Cerrado no dice nada: sólo el título y la flecha. El #723 le puso a cada
// encabezado un resumen de una línea ("05/10/26 10:25:41 → Sin salida
// registrada") y el usuario lo devolvió textual: "debería de estar colapsado y
// no mostrar info hasta que se abra". No hay prop de resumen a propósito, para
// que volver a ponerlo no sea un cambio de una línea.
//
// El encabezado es un <button> y no un <div onClick>: tiene que entrar en el
// orden de tabulación y responder a Enter/Espacio sin código propio, y
// `aria-expanded` + `aria-controls` es lo que un lector de pantalla anuncia.
// La región existe siempre en el DOM (con `hidden`) para que `aria-controls`
// no apunte a un id inexistente; lo que NO se monta cerrado es el contenido.
function CollapsibleSection({
  id,
  icon,
  title,
  open,
  onToggle,
  surface,
  labelColor,
  themeClasses,
  children,
}: {
  id: string;
  icon: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  surface: string;
  labelColor: string;
  themeClasses: any;
  children: ReactNode;
}) {
  const panelId = `${id}-panel`;
  return (
    <div className={`${surface} rounded-xl`}>
      <button
        type="button"
        id={`${id}-header`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${themeClasses.buttonHover} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50`}
      >
        <span
          className={`min-w-0 flex-1 text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}
        >
          <i aria-hidden="true" className={`${icon} mr-1`}></i>
          {title}
        </span>
        <i
          aria-hidden="true"
          className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg flex-shrink-0 ${labelColor}`}
        ></i>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={`${id}-header`}
        hidden={!open}
        className="px-4 pb-4 pt-1 space-y-3"
      >
        {open && children}
      </div>
    </div>
  );
}

// ── Photo Carousel Modal ─────────────────────────────────────────────────────

function PhotoCarouselModal({
  evidences,
  initialIndex,
  isDarkMode,
  onClose,
}: {
  evidences: YardInspectionEvidence[];
  initialIndex: number;
  isDarkMode: boolean;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(initialIndex);

  const prev = useCallback(
    () => setCurrent((i) => (i > 0 ? i - 1 : evidences.length - 1)),
    [evidences.length],
  );
  const next = useCallback(
    () => setCurrent((i) => (i < evidences.length - 1 ? i + 1 : 0)),
    [evidences.length],
  );

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [prev, next, onClose]);

  const photo = evidences[current];
  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`relative w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl ${isDarkMode ? "bg-[#1e1e1e]" : "bg-white"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Cerrar"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
        >
          <i className="ri-close-line text-sm"></i>
        </button>

        <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-black/50 text-white text-xs font-medium">
          {current + 1} / {evidences.length}
        </div>

        <div
          className={`relative flex items-center justify-center ${isDarkMode ? "bg-gray-800" : "bg-gray-100"} min-h-[320px] h-[50vh]`}
        >
          {evidences.length > 1 && (
            <button
              aria-label="Anterior"
              onClick={prev}
              className="absolute left-3 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
          )}

          <img
            src={photo.imageUrl}
            alt={photo.title || `Foto ${photo.order ?? current + 1}`}
            className="max-h-full max-w-full object-contain"
          />

          {evidences.length > 1 && (
            <button
              aria-label="Siguiente"
              onClick={next}
              className="absolute right-3 z-10 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          )}
        </div>

        <div
          className={`p-5 space-y-2 ${isDarkMode ? "bg-[#1e1e1e]" : "bg-white"}`}
        >
          <div className="flex items-center justify-between">
            <p
              className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {photo.title || (
                <span
                  className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                >
                  Sin título
                </span>
              )}
            </p>
            {photo.timestamp && (
              <span
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"} flex items-center gap-1`}
              >
                <i className="ri-time-line"></i>
                {formatYardTs(photo.timestamp)}
              </span>
            )}
          </div>
          {photo.description && (
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              {photo.description}
            </p>
          )}

          {evidences.length > 1 && (
            <div className="flex gap-2 pt-2 overflow-x-auto">
              {evidences.map((p, idx) => (
                <button
                  key={p.id}
                  onClick={() => setCurrent(idx)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all border-2 ${
                    idx === current ? "border-primary-500" : "border-transparent"
                  }`}
                >
                  <img
                    src={p.imageUrl}
                    alt={`Foto ${p.order ?? idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Exit-event picker ───────────────────────────────────────────────────────
//
// Inline panel rendered under the "Eventos de Acceso" row when the operator
// chooses to link a salida from inside the inspection modal. Renders two
// grouped lists of candidate events; clicking one links it to the inspection
// via the modal's existing `onSave`.

function eventSourceLabel(s: YardAccessEvent["source"]) {
  if (s === "hikvision") return "Hikvision";
  if (s === "manual") return "Manual";
  return "QR";
}

function ExitEventPicker({
  manualByPlate,
  sameVehicleUnlinked,
  loading,
  error,
  hasVehicleIdentifier,
  linkingId,
  themeClasses,
  isDarkMode,
  surface,
  labelColor,
  onLink,
  onCancel,
  onManualExit,
  manualExitBusy,
  manualExitForm,
  manualExitBlockers = [],
}: {
  manualByPlate: YardAccessEvent[];
  sameVehicleUnlinked: YardAccessEvent[];
  loading: boolean;
  error: string | null;
  hasVehicleIdentifier: boolean;
  linkingId: string | null;
  themeClasses: any;
  isDarkMode: boolean;
  surface: string;
  labelColor: string;
  onLink: (event: YardAccessEvent) => void;
  onCancel: () => void;
  // Opcional: sin él (un padre que no lo cablea) el botón no aparece.
  onManualExit?: () => void;
  manualExitBusy?: boolean;
  // El formulario del porqué, abierto debajo del botón (ver ManualExitForm).
  manualExitForm?: ReactNode;
  // Lo que le falta a la inspección para poder darle salida manual; con algo
  // aquí el botón queda deshabilitado y se lista (ver manualExitMissing).
  manualExitBlockers?: string[];
}) {
  const cardBg = isDarkMode ? "bg-[#1e1e1e]" : "bg-white";
  const isAnyLinking = linkingId !== null || !!manualExitBusy;
  const totalCandidates = manualByPlate.length + sameVehicleUnlinked.length;

  const renderRow = (event: YardAccessEvent) => {
    const isThisLinking = linkingId === event.id;
    return (
      <button
        key={event.id}
        type="button"
        onClick={() => onLink(event)}
        disabled={isAnyLinking}
        className={`w-full text-left flex items-center gap-3 p-2.5 rounded-lg border ${themeClasses.cardBorder} ${cardBg} transition-colors disabled:opacity-60 hover:ring-1 hover:ring-primary-500/40`}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-500/20">
          {isThisLinking ? (
            <i className="ri-loader-4-line animate-spin text-red-500"></i>
          ) : (
            <i className="ri-logout-box-line text-red-500"></i>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-sm ${themeClasses.text} truncate`}>
            {event.vehicle_plate || event.card_number || "—"}
            <span className={`text-xs ${themeClasses.textSecondary} ml-2`}>
              {formatYardTs(event.timestamp)}
            </span>
          </p>
          <p
            className={`text-[11px] ${themeClasses.textSecondary} truncate`}
          >
            {eventSourceLabel(event.source)}
            {event.yard_name ? ` · ${event.yard_name}` : ""}
            {event.driver_name ? ` · ${event.driver_name}` : ""}
          </p>
        </div>
        <i
          className={`ri-link text-base ${labelColor} flex-shrink-0`}
        ></i>
      </button>
    );
  };

  return (
    <div className={`${surface} rounded-xl p-3 space-y-3 mt-1`}>
      <div className="flex items-center justify-between">
        <p
          className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}
        >
          <i className="ri-link mr-1"></i>Vincular evento de salida
        </p>
        <button
          type="button"
          onClick={onCancel}
          className={`text-[11px] ${labelColor} hover:${themeClasses.text} transition-colors`}
        >
          Cerrar
        </button>
      </div>

      {/* Sin evento que ligar —la lectora de salida no leyó a la unidad, o la
          pluma se levantó a mano—, el cierre era ir a Patio de Maniobras,
          registrar una salida manual y volver a ligarla. Este botón hace las
          tres cosas sobre ESTA visita y además levanta la pluma de salida. */}
      {onManualExit && (
        <button
          type="button"
          onClick={onManualExit}
          disabled={isAnyLinking || manualExitBlockers.length > 0}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold tracking-wide transition-all bg-red-500/15 hover:bg-red-500/25 text-red-500 border border-red-500/40 disabled:opacity-60"
        >
          {manualExitBusy ? (
            <i className="ri-loader-4-line animate-spin"></i>
          ) : (
            <i className="ri-logout-box-r-line text-base"></i>
          )}
          DAR SALIDA MANUAL
        </button>
      )}
      {onManualExit && manualExitBlockers.length > 0 && (
        // La salida manual cierra la visita y levanta la pluma: una descarga sin
        // capturar se iría del patio sin registro. Se dice qué falta en vez de
        // sólo apagar el botón.
        <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10">
          <i className="ri-error-warning-line text-amber-500 mt-0.5"></i>
          <div className={`text-xs ${themeClasses.textSecondary}`}>
            <p className="font-medium mb-0.5">Completa la inspección para dar salida manual. Falta:</p>
            <ul className="list-disc ml-4">
              {manualExitBlockers.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {manualExitForm}

      {!hasVehicleIdentifier ? (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg ${cardBg} border ${themeClasses.cardBorder}`}
        >
          <i className={`ri-information-line ${labelColor}`}></i>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            Asigna primero un vehículo o placa para ver salidas vinculables.
          </p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-6">
          <i className="ri-loader-4-line animate-spin text-primary-500 text-xl"></i>
        </div>
      ) : error ? (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg ${cardBg} border border-red-500/30`}
        >
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-xs text-red-400">{error}</p>
        </div>
      ) : totalCandidates === 0 ? (
        // ── Por qué esta lista puede salir vacía ──────────────────────────
        // /yardInspection/candidateExits sólo devuelve eventos que YA son
        // `event_type === "exit"` y que no estén ligados a otra visita. Para un
        // tag que nunca ha pasado por la lectora de salida no existe ni un solo
        // evento así, por muchas veces que haya cruzado la pluma: en producción
        // 11 de los 20 tags de Acuapue están en esa situación, y SN70317 lleva
        // 35 lecturas, todas por la lectora de ENTRADA. Vacío aquí no significa
        // "no encontramos el evento", significa "el carril de salida nunca leyó
        // a esta unidad". El único cierre posible es un evento manual.
        <div
          className={`flex items-start gap-2 p-3 rounded-lg ${cardBg} border ${themeClasses.cardBorder}`}
        >
          <i className={`ri-information-line ${labelColor} mt-0.5`}></i>
          <p className={`text-xs leading-relaxed ${themeClasses.textSecondary}`}>
            No hay ningún evento de salida que ligar: la lectora de salida nunca
            leyó a esta unidad. Usa «Dar salida manual» para cerrar la visita.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {manualByPlate.length > 0 && (
            <div className="space-y-1.5">
              <p
                className={`text-[10px] uppercase tracking-wider ${labelColor} font-medium`}
              >
                Manual · placa coincidente ({manualByPlate.length})
              </p>
              <div className="space-y-1.5">
                {manualByPlate.map(renderRow)}
              </div>
            </div>
          )}
          {sameVehicleUnlinked.length > 0 && (
            <div className="space-y-1.5">
              <p
                className={`text-[10px] uppercase tracking-wider ${labelColor} font-medium`}
              >
                Salidas del vehículo sin inspección (
                {sameVehicleUnlinked.length})
              </p>
              <div className="space-y-1.5">
                {sameVehicleUnlinked.map(renderRow)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * El porqué de una salida manual, antes de confirmarla: por qué la lectora de
 * salida no la leyó. Mismas preguntas que el paso 1 de una pipa sin tag
 * (TagSurveyFields); con mode "position" —la unidad entró leída por su tag—
 * sólo dónde va el tag. Confirmar crea la salida, cierra la visita y levanta
 * la pluma: es la confirmación de las tres cosas.
 */
function ManualExitForm({
  mode,
  survey,
  onChange,
  busy,
  onConfirm,
  onCancel,
  themeClasses,
  isDarkMode,
  loading,
}: {
  mode: ExitTagSurveyMode;
  // Mientras se lee el evento de entrada no se sabe qué preguntar.
  loading: boolean;
  survey: TagSurvey;
  onChange: (survey: TagSurvey) => void;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  themeClasses: any;
  isDarkMode: boolean;
}) {
  const complete = exitTagSurveyComplete(mode, survey);
  return (
    <div className={`rounded-xl p-3 space-y-3 border border-red-500/30 ${isDarkMode ? "bg-[#1e1e1e]" : "bg-white"}`}>
      <p className={`text-xs leading-relaxed ${themeClasses.textSecondary}`}>
        {mode === "position"
          ? "La unidad entró leída por su tag: la lectora de salida no lo leyó. Indica dónde va el tag en el parabrisas."
          : "Indica por qué la lectora no leyó a la unidad: si trae tag y, si lo trae, dónde va en el parabrisas."}
      </p>
      {loading ? (
        <div className="flex justify-center py-3">
          <i className="ri-loader-4-line animate-spin text-primary-500"></i>
        </div>
      ) : (
      <TagSurveyFields
        survey={survey}
        onChange={onChange}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        positionOnly={mode === "position"}
        idPrefix="manual-exit"
      />
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className={`flex-1 py-2 rounded-xl text-sm font-medium border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover} disabled:opacity-60`}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={loading || !complete || busy}
          className="flex-[2] py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
        >
          {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-logout-box-r-line"></i>}
          Confirmar salida y levantar pluma
        </button>
      </div>
    </div>
  );
}

// ── Detail / Edit Modal ──────────────────────────────────────────────────────

export interface YardInspectionDetailsModalProps {
  inspection: YardInspection;
  themeClasses: any;
  isDarkMode: boolean;
  vehicles: TruckYardVehicle[];
  // Zona IANA del patio de ESTA inspección, para las horas con segundos del
  // recorrido de la visita. Opcional: sin ella se usa la del navegador, igual que
  // `formatYardTs`, en vez de suponer una.
  yardTimeZone?: string;
  onClose: () => void;
  onSave: (data: UpdateYardInspectionParams) => Promise<void>;
  // Paso 1 de una pipa sin tag (`PUT /yardInspection/registration`). Devuelve
  // si se guardó: a diferencia de `onSave`, el modal necesita saberlo para
  // decidir si avanza al paso 2. Opcional porque sólo lo usa ese tipo de
  // inspección; sin él, el paso 1 no puede guardar.
  onSaveRegistration?: (
    data: UpdateYardInspectionRegistrationParams,
  ) => Promise<boolean>;
  onUploadEvidence: (
    file: File,
    opts?: { title?: string; description?: string },
  ) => Promise<void>;
  onUpdateEvidence?: (
    evidenceId: string,
    patch: { title?: string; description?: string },
  ) => Promise<void>;
  onDeleteEvidence: (evidenceId: string) => Promise<void>;
  // "DAR SALIDA MANUAL" (POST /yardInspection/manualExit + pluma de salida).
  // Devuelve si se registró la salida. Opcional: sin él el botón no aparece.
  onManualExit?: (
    inspection: YardInspection,
    survey: { tag_presence?: string | null; tag_position?: string | null },
  ) => Promise<boolean>;
}

/**
 * El modal de inspección, según de dónde salió la visita:
 *
 *  · tag de proveedores del patio → `ProviderVisitModal`: tres campos y nada
 *    más.
 *  · pipa sin tag → dos pasos: el registro manual y después el formulario de
 *    siempre.
 *  · cualquier otra —incluido el proveedor legacy de `is_provider`, que nace
 *    cancelado— → el formulario de siempre, sin cambios.
 *
 * La decisión se toma acá, ANTES de montar ninguno, y no con condicionales
 * adentro del formulario normal: ese componente dispara al montarse el fetch
 * de geocerca y mantiene el estado de fotos y parámetros de agua, que el modal
 * de proveedor no debe ni pedir ni mostrar.
 */
export default function YardInspectionDetailsModal(
  props: YardInspectionDetailsModalProps,
) {
  const kind = specialInspectionKind(props.inspection);
  if (kind === "provider") {
    return (
      <ProviderVisitModal
        inspection={props.inspection}
        themeClasses={props.themeClasses}
        isDarkMode={props.isDarkMode}
        yardTimeZone={props.yardTimeZone}
        onClose={props.onClose}
        onSave={props.onSave}
        onManualExit={props.onManualExit}
      />
    );
  }
  if (kind === "untagged") return <UntaggedInspectionModal {...props} />;
  return <StandardInspectionModal {...props} />;
}

function UntaggedInspectionModal(props: YardInspectionDetailsModalProps) {
  const { inspection } = props;
  const [requestedStep, setRequestedStep] = useState<UntaggedStep>(() =>
    untaggedInitialStep(inspection),
  );
  // El padre puede cambiar de inspección sin desmontar: la nueva abre en SU
  // paso, no en el que quedó la anterior.
  useEffect(() => {
    setRequestedStep(untaggedInitialStep(inspection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspection.id]);

  // Derivado y no sólo guardado: si la inspección deja de tener el registro
  // (otra sesión la corrigió, llegó un documento más viejo por el poll), el
  // paso 2 no puede quedar abierto sobre una unidad sin identificar.
  const step: UntaggedStep = canOpenUntaggedStep(inspection, requestedStep)
    ? requestedStep
    : 1;

  if (step === 1) {
    return (
      <UntaggedRegistrationStep
        inspection={inspection}
        themeClasses={props.themeClasses}
        isDarkMode={props.isDarkMode}
        vehicles={props.vehicles}
        onClose={props.onClose}
        onSaveRegistration={props.onSaveRegistration}
        onGoToStep={setRequestedStep}
      />
    );
  }

  return (
    <StandardInspectionModal
      {...props}
      headerBadge={<SpecialTagBadge kind="untagged" className="flex-shrink-0" />}
      stepHeader={
        <UntaggedStepIndicator
          current={2}
          canOpenStep2={untaggedRegistrationComplete(inspection)}
          onSelect={setRequestedStep}
          themeClasses={props.themeClasses}
          isDarkMode={props.isDarkMode}
        />
      }
    />
  );
}

function StandardInspectionModal({
  inspection,
  themeClasses,
  isDarkMode,
  vehicles,
  yardTimeZone,
  onClose,
  onSave,
  onUploadEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onManualExit,
  stepHeader,
  headerBadge,
}: YardInspectionDetailsModalProps & {
  // Sólo para la pipa sin tag: el indicador de pasos arriba del cuerpo y su
  // insignia junto al estado. Una inspección normal no pasa ninguno de los dos.
  stepHeader?: ReactNode;
  headerBadge?: ReactNode;
}) {
  const navigate = useNavigate();
  const status = deriveInspectionStatus(inspection);
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const labelColor = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;

  // La sección de notificaciones vive en `/admin/notifications`, y `/admin`
  // está vedado por rol: supervisor, inspector, vigilante y admin de patios
  // —roles que sí tienen la app de inspecciones— rebotan a `/map` en el guard
  // de la ruta. A ellos el enlace no se les dibuja: uno que aterriza en el mapa
  // sin decir por qué es peor que no tenerlo. Se lee igual que el guard (store,
  // con localStorage de respaldo mientras el usuario todavía no hidrató).
  const userRoleId = useUserStore((state) => state.user?.role_id);
  const canOpenNotifications = canEnterAdminApp(
    userRoleId ?? localStorage.getItem("userRoleId"),
  );

  const openAccessEvent = (eventId: string | null | undefined) => {
    if (!eventId) return;
    onClose();
    navigate(`/truck-yard/access?event=${eventId}`);
  };

  const [vehiclePlate, setVehiclePlate] = useState(
    inspection.vehicle_plate || "",
  );
  const [vehicleId, setVehicleId] = useState(inspection.vehicle_id || "");
  const [driverName, setDriverName] = useState(inspection.driver_name || "");
  const [waterType, setWaterType] = useState(inspection.water_type || "");
  const [waterTypeReported, setWaterTypeReported] = useState(
    inspection.water_type_reported || "",
  );
  const [volumenExtraido, setVolumenExtraido] = useState(
    inspection.volumen_extraido || "",
  );
  // Lecturas del medidor, una por slot de foto. Se guardan como string sin
  // normalizar (ver WATER_PARAMETERS): "" significa "no capturado", que es
  // distinto de 0 y tiene que seguir siéndolo aguas abajo.
  const [waterValues, setWaterValues] = useState<Record<PhotoSlotKey, string>>(
    () =>
      Object.fromEntries(
        WATER_PARAMETERS.map((p) => [p.key, inspection[p.field] || ""]),
      ) as Record<PhotoSlotKey, string>,
  );
  // Ya no se editan desde este modal (ver el bloque oculto más abajo), pero se
  // siguen leyendo y reenviando tal cual para no borrar lo ya capturado.
  const title = inspection.title || "";
  const description = inspection.description || "";
  const notes = inspection.notes || "";
  const [saving, setSaving] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState<number | null>(null);
  // Destino de la foto que se está tomando con la cámara. `null` = visor
  // cerrado; "__extra__" = la foto va a "Otras fotografías".
  const [cameraTarget, setCameraTarget] = useState<
    { key: PhotoSlotKey; label: string } | "__extra__" | null
  >(null);

  // ── Geofence event (lazily resolved on open) ─────────────────────────────
  const [geofenceEvent, setGeofenceEvent] =
    useState<InspectionGeofenceEvent | null>(null);
  const [geofenceLoading, setGeofenceLoading] = useState(true);
  const [geofenceReason, setGeofenceReason] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setGeofenceLoading(true);
    setGeofenceEvent(null);
    setGeofenceReason(null);
    fetchInspectionGeofenceEvent(inspection.id)
      .then((res) => {
        if (cancelled) return;
        setGeofenceEvent(res.data);
        setGeofenceReason(res.reason ?? null);
      })
      .catch(() => {
        if (!cancelled) setGeofenceReason("error");
      })
      .finally(() => {
        if (!cancelled) setGeofenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inspection.id]);

  const [slotDescriptions, setSlotDescriptions] = useState<
    Record<PhotoSlotKey, string>
  >({ ph: "", conductividad: "", turbidez: "" });

  // Per-evidence description drafts so the input stays visible (and editable)
  // after a photo is uploaded. We only persist on blur to avoid spamming the
  // API on every keystroke.
  const [evidenceDescriptionDrafts, setEvidenceDescriptionDrafts] = useState<
    Record<string, string>
  >({});
  const [savingEvidenceId, setSavingEvidenceId] = useState<string | null>(null);

  const getEvidenceDescriptionValue = (ev: YardInspectionEvidence) =>
    evidenceDescriptionDrafts[ev.id] ?? ev.description ?? "";

  const handleEvidenceDescriptionBlur = async (ev: YardInspectionEvidence) => {
    if (!onUpdateEvidence) return;
    const draft = evidenceDescriptionDrafts[ev.id];
    if (draft === undefined) return;
    if (draft === (ev.description ?? "")) {
      setEvidenceDescriptionDrafts((prev) => {
        const next = { ...prev };
        delete next[ev.id];
        return next;
      });
      return;
    }
    setSavingEvidenceId(ev.id);
    try {
      await onUpdateEvidence(ev.id, { description: draft });
      setEvidenceDescriptionDrafts((prev) => {
        const next = { ...prev };
        delete next[ev.id];
        return next;
      });
    } finally {
      setSavingEvidenceId(null);
    }
  };

  // ── Exit-event picker ───────────────────────────────────────────────────
  // Lets the operator link an existing exit event (manual-by-plate or
  // unlinked-by-vehicle) without leaving this modal.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidates, setCandidates] = useState<YardAccessEvent[] | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const hasVehicleIdentifier =
    !!inspection.vehicle_id || !!inspection.vehicle_plate;

  useEffect(() => {
    if (!pickerOpen) return;
    if (!hasVehicleIdentifier) {
      // Nothing to match against — render empty state without an API call.
      setCandidates([]);
      setLoadingCandidates(false);
      setCandidatesError(null);
      return;
    }

    let cancelled = false;
    setLoadingCandidates(true);
    setCandidatesError(null);

    const entryMs = toYardMillis(inspection.entry_timestamp);
    const fromMs = entryMs || Date.now() - 14 * 24 * 60 * 60 * 1000;

    fetchYardAccessEvents({
      yard_id: inspection.yard_id,
      event_type: "exit",
      date_from: new Date(fromMs).toISOString(),
      limit: 50,
    })
      .then((res) => {
        if (!cancelled) setCandidates(res.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setCandidatesError(
            err instanceof Error
              ? err.message
              : "No se pudieron cargar los eventos",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidates(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    pickerOpen,
    hasVehicleIdentifier,
    inspection.yard_id,
    inspection.entry_timestamp,
  ]);

  // Manual-by-plate wins when an event qualifies for both groups.
  const { manualByPlate, sameVehicleUnlinked } = useMemo(() => {
    const all = candidates ?? [];
    const platesEqual = (a?: string | null, b?: string | null) =>
      !!a && !!b && a.trim().toUpperCase() === b.trim().toUpperCase();

    const manual = all.filter(
      (e) =>
        e.source === "manual" &&
        platesEqual(e.vehicle_plate, inspection.vehicle_plate),
    );
    const manualIds = new Set(manual.map((e) => e.id));

    const unlinked = all.filter(
      (e) =>
        !manualIds.has(e.id) &&
        !e.inspection_id &&
        !!inspection.vehicle_id &&
        e.vehicle_id === inspection.vehicle_id,
    );

    return { manualByPlate: manual, sameVehicleUnlinked: unlinked };
  }, [candidates, inspection.vehicle_id, inspection.vehicle_plate]);

  const [manualExitBusy, setManualExitBusy] = useState(false);
  // "DAR SALIDA MANUAL" abre primero el porqué: igual que el paso 1 de una pipa
  // sin tag, la salida no se registra sin decir por qué la lectora no la leyó.
  // Si la unidad ENTRÓ leída por su tag, se sabe que lo trae y falló la lectora
  // de salida: sólo se pregunta dónde va (exitTagSurveyMode).
  const [manualExitFormOpen, setManualExitFormOpen] = useState(false);
  const [exitSurvey, setExitSurvey] = useState<TagSurvey>({ tag_presence: "", tag_position: "" });
  // Origen del evento de ENTRADA: decide si la unidad entró leída por su tag
  // (exitTagSurveyMode). `undefined` = cargando; `null` = no se pudo saber, y
  // entonces se preguntan las dos cosas, que el backend siempre acepta.
  const [entrySource, setEntrySource] = useState<string | null | undefined>(undefined);
  const exitSurveyMode = exitTagSurveyMode(inspection, entrySource);
  // Se reinicia al cambiar de visita Y al cerrar el panel: si no, al reabrirlo
  // el formulario aparecía ya abierto y contestado, y un solo clic levantaba la
  // pluma con respuestas viejas.
  useEffect(() => {
    if (pickerOpen) return;
    setManualExitFormOpen(false);
    setExitSurvey({ tag_presence: "", tag_position: "" });
  }, [inspection.id, pickerOpen]);
  useEffect(() => {
    if (!manualExitFormOpen) return;
    const entryId = inspection.access_entry_id;
    if (!entryId) {
      setEntrySource(null);
      return;
    }
    let cancelled = false;
    setEntrySource(undefined);
    fetchYardAccessEvent(entryId)
      .then((ev) => { if (!cancelled) setEntrySource(ev?.source ?? null); })
      .catch(() => { if (!cancelled) setEntrySource(null); });
    return () => { cancelled = true; };
  }, [manualExitFormOpen, inspection.access_entry_id]);
  const confirmManualExit = async () => {
    if (!onManualExit || !exitTagSurveyComplete(exitSurveyMode, exitSurvey)) return;
    setManualExitBusy(true);
    try {
      if (await onManualExit(inspection, exitTagSurveyPayload(exitSurveyMode, exitSurvey))) {
        setManualExitFormOpen(false);
        setPickerOpen(false);
      }
    } finally {
      setManualExitBusy(false);
    }
  };

  const handleLinkExit = async (event: YardAccessEvent) => {
    setLinkingId(event.id);
    try {
      await onSave({
        id: inspection.id,
        access_exit_id: event.id,
        exit_timestamp: event.timestamp,
      });
      setPickerOpen(false);
    } finally {
      setLinkingId(null);
    }
  };

  // ── "Accesos y geocerca": un solo grupo, cerrado al abrir ────────────────
  // El historial monta el modal con `{selectedInspection && …}`, así que cada
  // apertura nace con este estado inicial; el efecto de abajo cubre el otro
  // camino —que el padre cambie de inspección sin desmontar—, donde el
  // `useState` no se reinicia solo y la visita nueva heredaría abierto el
  // grupo de la anterior.
  const [journeyOpenState, setJourneyOpenState] = useState(false);
  useEffect(() => {
    setJourneyOpenState(false);
  }, [inspection.id]);

  // El selector de salida vive DENTRO del grupo. Hoy sólo lo abren botones que
  // están ahí mismo, así que no hay forma de tenerlo abierto con el grupo
  // cerrado — pero si algún día algo lo abre desde afuera (el aviso de "No se
  // registró la salida", un deep-link), un selector montado en una región
  // oculta sería un fetch de candidatos que nadie ve. Derivarlo acá lo hace
  // imposible en vez de improbable; y al cerrar el grupo se cierra el
  // selector, o el encabezado quedaría sin poder cerrarlo.
  const journeyOpen = journeyOpenState || pickerOpen;
  const toggleJourney = () => {
    if (journeyOpen) setPickerOpen(false);
    setJourneyOpenState(!journeyOpen);
  };

  // Los cuatro momentos, en orden. De dónde sale cada hora de geocerca según
  // el estado del fetch lo decide `visitJourney` (puro, con su test).
  const journey = visitJourney(
    inspection,
    {
      loading: geofenceLoading,
      failed: geofenceReason === "error",
      event: geofenceEvent,
    },
    yardTimeZone,
  );
  const yardName = modalYardName(inspection.yard_name);


  const selectStyles = getCustomSelectStyles(isDarkMode);

  type SelectOpt = { value: string; label: string };
  const vehicleOptions: SelectOpt[] = vehicles.map((v) => ({
    value: v.id,
    label: v.plate ? `${v.name || v.id} · ${v.plate}` : v.name || v.id,
  }));
  // Whether this inspection already has a vehicle assigned. When it does, the
  // vehicle picker is locked (read-only) showing that unit; when it doesn't,
  // the picker is enabled so the operator can assign one.
  const hasAssignedVehicle = !!inspection.vehicle_id;
  // Resolve the current option; synthesize a fallback from the inspection's own
  // plate so the assigned unit still shows even if the vehicles list hasn't
  // loaded (or the unit isn't in it).
  const selectedVehicleOption: SelectOpt | null =
    vehicleOptions.find((o) => o.value === vehicleId) ||
    (vehicleId
      ? {
          value: vehicleId,
          label: vehiclePlate || inspection.vehicle_plate || vehicleId,
        }
      : null);

  // ── Tipo de agua REPORTADA = el/los tipo(s) que el vehículo tiene asignado /
  //    heredado de su grupo (truck_yard_types ∩ tipos del grupo). El operador
  //    solo puede elegir entre esos; si es uno solo, queda fijado.
  const groups = useGroupsStore((s) => s.groups);
  useAllGroups();
  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === vehicleId) || null,
    [vehicles, vehicleId],
  );
  const vehicleWaterTypes = useMemo(() => {
    const grp = groups.find((g) => (g.vehicle_ids || []).includes(vehicleId));
    return effectiveWaterTypes(selectedVehicle?.truck_yard_types, grp?.type ?? []);
  }, [groups, vehicleId, selectedVehicle]);
  // Keep the reported type in sync with the vehicle: preserve a still-valid
  // choice, otherwise snap to the vehicle's (first) type.
  useEffect(() => {
    if (vehicleWaterTypes.length === 0) return;
    setWaterTypeReported((prev) =>
      prev && vehicleWaterTypes.includes(prev) ? prev : vehicleWaterTypes[0],
    );
  }, [vehicleWaterTypes]);

  const handleVehicleChange = (option: SelectOpt | null) => {
    if (option) {
      setVehicleId(option.value);
      const v = vehicles.find((veh) => veh.id === option.value);
      if (v?.plate) setVehiclePlate(v.plate);
    } else {
      setVehicleId("");
      setVehiclePlate("");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        id: inspection.id,
        vehicle_id: vehicleId || null,
        vehicle_plate: vehiclePlate || null,
        driver_name: driverName || null,
        water_type: waterType || null,
        water_type_reported: waterTypeReported || null,
        volumen_extraido: volumenExtraido || null,
        ph: waterValues.ph.trim() || null,
        conductividad: waterValues.conductividad.trim() || null,
        turbidez: waterValues.turbidez.trim() || null,
        title: title || null,
        description: description || null,
        notes: notes || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const uploadSlotPhoto = async (
    slotKey: PhotoSlotKey,
    slotLabel: string,
    file: File,
  ) => {
    setUploadingSlot(slotKey);
    try {
      await onUploadEvidence(file, {
        title: slotLabel,
        description: slotDescriptions[slotKey] || undefined,
      });
      setSlotDescriptions((prev) => ({ ...prev, [slotKey]: "" }));
    } finally {
      setUploadingSlot(null);
    }
  };

  const uploadExtraPhoto = async (file: File) => {
    setUploadingSlot("__extra__");
    try {
      await onUploadEvidence(file);
    } finally {
      setUploadingSlot(null);
    }
  };

  // Foto tomada desde el visor de cámara: se cierra el visor y se sube al
  // destino que lo abrió.
  const handleCameraCapture = (file: File) => {
    const target = cameraTarget;
    setCameraTarget(null);
    if (!target) return;
    if (target === "__extra__") void uploadExtraPhoto(file);
    else void uploadSlotPhoto(target.key, target.label, file);
  };

  const handleDeletePhoto = async (evidenceId: string) => {
    const result = await Swal.fire({
      title: "¿Eliminar foto?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    await onDeleteEvidence(evidenceId);
  };

  // Solo los campos que este modal EDITA. title/description/notes se leen de
  // `inspection` y se reenvían intactos (ver arriba), así que compararlos
  // contra su propio origen siempre daba false: se leían como checks vivos sin
  // serlo. Si alguna vez vuelven a ser editables, vuelven aquí junto con su
  // estado.
  const hasChanges =
    vehiclePlate !== (inspection.vehicle_plate || "") ||
    vehicleId !== (inspection.vehicle_id || "") ||
    driverName !== (inspection.driver_name || "") ||
    waterType !== (inspection.water_type || "") ||
    waterTypeReported !== (inspection.water_type_reported || "") ||
    volumenExtraido !== (inspection.volumen_extraido || "") ||
    WATER_PARAMETERS.some(
      (p) => waterValues[p.key] !== (inspection[p.field] || ""),
    );

  // Qué falta para "DAR SALIDA MANUAL", calculado una vez. Se mide sobre lo
  // GUARDADO (es lo que el backend va a revisar); con cambios sin guardar se
  // dice eso primero, en vez de listar como faltante algo que ya se tecleó.
  const manualExitBlockers = [
    ...(hasChanges ? ["Cambios sin guardar: guarda la inspección primero"] : []),
    ...manualExitMissing(inspection),
  ];
  // Si deja de estar completa con el formulario abierto (otra sesión borró una
  // foto, se editó un campo), se cierra y se reinicia: al volver a estar
  // completa no puede reaparecer contestado, a un clic de levantar la pluma.
  const manualExitBlocked = manualExitBlockers.length > 0;
  useEffect(() => {
    if (!manualExitBlocked) return;
    setManualExitFormOpen(false);
    setExitSurvey({ tag_presence: "", tag_position: "" });
  }, [manualExitBlocked]);

  const evidences = inspection.evidences ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-gray-700/30 bg-inherit rounded-t-2xl">
          {/* El patio va EN el título: era una sección propia para un solo
              dato, y es lo primero que hay que saber de la inspección. En
              segunda línea y no "Inspección de Descarga · Acuapue" de corrido:
              a 320 px el título, el pill de estado y la X ya llenan el
              renglón, y un nombre largo empujaba el pill fuera. El separador
              oculto es para que el encabezado se anuncie "Inspección de
              Descarga · Acuapue" y no las dos palabras pegadas. */}
          <div
            className={`flex items-center gap-3 min-w-0 ${
              // Con la insignia son tres piezas además del título: a 320 px no
              // caben en un renglón, y sin wrap la insignia empujaba la X.
              headerBadge ? "flex-wrap" : ""
            }`}
          >
            <h2 className={`min-w-0 text-base font-bold ${themeClasses.text}`}>
              <span className="block">Inspección de Descarga</span>
              {yardName && (
                <span
                  className={`block truncate text-xs font-medium ${themeClasses.textSecondary}`}
                >
                  <span className="sr-only"> · </span>
                  <i aria-hidden="true" className="ri-building-4-line mr-1"></i>
                  {yardName}
                </span>
              )}
            </h2>
            <span
              className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${inspectionStatusClasses(status)}`}
            >
              <i className={`${inspectionStatusIcon(status)} text-xs`}></i>
              {inspectionStatusLabel(status)}
            </span>
            {headerBadge}
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {stepHeader}
          {status === "missing_exit" && (
            <div
              className={`rounded-xl p-3 flex items-start gap-2.5 ${
                isDarkMode
                  ? "bg-orange-500/10 border border-orange-500/25"
                  : "bg-orange-50 border border-orange-200"
              }`}
            >
              <i
                className={`ri-question-line text-base mt-0.5 ${
                  isDarkMode ? "text-orange-400" : "text-orange-600"
                }`}
              ></i>
              <p
                className={`text-xs leading-relaxed ${
                  isDarkMode ? "text-orange-300" : "text-orange-800"
                }`}
              >
                <strong>No se registró la salida.</strong>{" "}
                {inspection.exit_missing_reason ? (
                  // Razón observada por el backend: el mismo tag volvió a pasar
                  // con esta visita abierta. Es un hecho, no una estimación, así
                  // que se muestra tal cual en vez del texto por antigüedad.
                  inspection.exit_missing_reason
                ) : (
                  <>
                    La visita lleva más de {MISSING_EXIT_AFTER_HOURS} h abierta,
                    muy por encima de lo que dura una descarga.
                  </>
                )}{" "}
                Lo más probable es que la unidad ya se haya ido y el carril de
                salida no la haya leído — no que siga en el patio. Abre{" "}
                <strong>Accesos y geocerca</strong> aquí abajo para ligar la
                salida, o registra una manual si no aparece ninguna.
              </p>
            </div>
          )}

          {status === "cancelled" && (
            <div
              className={`rounded-xl p-3 flex items-start gap-2.5 ${
                isDarkMode
                  ? "bg-gray-500/10 border border-gray-500/25"
                  : "bg-gray-100 border border-gray-200"
              }`}
            >
              <i
                className={`ri-information-line text-base mt-0.5 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              ></i>
              <p
                className={`text-xs leading-relaxed ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Visita de <strong>proveedor</strong>: la unidad cruzó la pluma
                pero no descarga agua, así que no hay inspección que realizar.
                El folio y los eventos de acceso quedan registrados; los campos
                de abajo son opcionales.
              </p>
            </div>
          )}

          {/* Accesos y geocerca (sólo lectura): un grupo, cerrado por defecto.
              Adentro, primero el recorrido —los cuatro momentos cruzados— y
              debajo el detalle de cada sistema SIN repetir las horas: lo que
              el recorrido ya dice, el detalle no lo vuelve a decir. */}
          <CollapsibleSection
            id="inspection-journey"
            icon="ri-route-line"
            title="Accesos y geocerca"
            open={journeyOpen}
            onToggle={toggleJourney}
            surface={surface}
            labelColor={labelColor}
            themeClasses={themeClasses}
          >
            {/* El color y la etiqueta dicen QUIÉN midió cada momento: verde
                azulado = GPS del rastreador (el mismo tono que ya usa el chip
                de la geocerca), primario = pluma. Van las dos señales porque
                el color solo no alcanza para quien no lo distingue.
                La hora hace wrap debajo del nombre en vez de truncarse: a
                320 px "Salida de la geocerca  GPS  05/10/26 11:24:55" no cabe
                en un renglón, y lo que se cortaba eran los segundos. */}
            <ol aria-label="Recorrido de la visita" className="space-y-2.5">
              {journey.map((step) => {
                const isGps = step.source === "geofence";
                const tone = isGps
                  ? isDarkMode
                    ? "bg-teal-500/20 text-teal-300"
                    : "bg-teal-50 text-teal-700"
                  : "bg-primary-500/15 text-primary-500";
                const notificationPath = canOpenNotifications
                  ? notificationDeepLinkPath(step.notificationId)
                  : null;
                return (
                  <li key={step.key} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold ${tone}`}
                    >
                      {step.step}
                    </span>
                    <div className="min-w-0 flex-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <span
                        className={`text-sm ${themeClasses.text} flex items-center gap-2`}
                      >
                        {step.title}
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-semibold ${tone}`}
                        >
                          <i
                            aria-hidden="true"
                            className={
                              isGps ? "ri-map-pin-2-line" : "ri-door-lock-line"
                            }
                          ></i>
                          {step.sourceLabel}
                        </span>
                      </span>
                      <span
                        className={`text-sm tabular-nums ${
                          step.missing
                            ? themeClasses.textSecondary
                            : `font-medium ${themeClasses.text}`
                        }`}
                      >
                        {step.text}
                      </span>
                      {/* La hora de arriba es una cota ("antes de…"), no una
                          lectura: el GPS reportó la unidad adentro DESPUÉS de
                          que la pluma leyera el tag. La nota dice qué midió el
                          GPS, para que la cota no esconda el dato crudo. */}
                      {step.note && (
                        <span
                          className={`basis-full text-[11px] ${themeClasses.textSecondary}`}
                        >
                          {step.note}
                        </span>
                      )}
                      {/* El cruce de geocerca disparó una notificación, y este
                          enlace abre ESA en su sección. Es un <a> de verdad
                          (Link) y no un botón con navigate(): es un
                          hipervínculo, así que responde a Enter, a "abrir en
                          pestaña nueva" y al lector de pantalla como uno.
                          Misma pestaña y cerrando el modal, igual que
                          `openAccessEvent`. El aria-label nombra el cruce
                          porque los dos enlaces dicen lo mismo a la vista y,
                          sueltos en la lista de enlaces del lector, serían
                          indistinguibles.

                          El modal sólo se cierra si el clic navega EN ESTA
                          pestaña. Con Ctrl/Cmd/Shift/clic del medio la
                          notificación se abre en otra y la inspección —con lo
                          que el usuario llevara escrito sin guardar— tiene que
                          seguir aquí; cerrarla igual era perderle el contexto
                          por haber usado justo lo que un <a> permite. */}
                      {notificationPath && (
                        <Link
                          to={notificationPath}
                          onClick={(event: ReactMouseEvent<HTMLAnchorElement>) => {
                            if (
                              event.button !== 0 ||
                              event.metaKey ||
                              event.ctrlKey ||
                              event.shiftKey ||
                              event.altKey
                            ) {
                              return;
                            }
                            onClose();
                          }}
                          aria-label={`Ver notificación de la ${step.title.toLowerCase()}`}
                          className="basis-full w-fit inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50"
                        >
                          <i
                            aria-hidden="true"
                            className="ri-notification-3-line"
                          ></i>
                          Ver notificación
                          <i
                            aria-hidden="true"
                            className="ri-arrow-right-up-line text-[10px]"
                          ></i>
                        </Link>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            {/* La zona con la que se leen las cuatro horas: es lo que explica
                una hora "corrida" a quien mira desde otra zona. */}
            {yardTimeZone && (
              <p className={`text-[11px] ${themeClasses.textSecondary}`}>
                <i aria-hidden="true" className="ri-time-zone-line mr-1"></i>
                Horas en zona del patio: {yardTimeZone}
              </p>
            )}

            {/* ── Pluma: los eventos de acceso detrás de los pasos 2 y 3 ── */}
            <div
              className={`pt-3 border-t ${themeClasses.cardBorder} space-y-3`}
            >
              <p
                className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium`}
              >
                <i aria-hidden="true" className="ri-door-lock-line mr-1"></i>
                Eventos de Acceso · Pluma
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => openAccessEvent(inspection.access_entry_id)}
                  disabled={!inspection.access_entry_id}
                  title={
                    inspection.access_entry_id
                      ? "Ver evento de acceso"
                      : undefined
                  }
                  className={`text-left rounded-lg -m-1 p-1 transition-colors ${
                    inspection.access_entry_id
                      ? `cursor-pointer ${themeClasses.buttonHover} hover:ring-1 hover:ring-primary-500/40`
                      : "cursor-default"
                  }`}
                >
                  <p
                    className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1 flex items-center gap-1`}
                  >
                    Entrada
                    {inspection.access_entry_id && (
                      <i className="ri-arrow-right-up-line text-[10px]"></i>
                    )}
                  </p>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        inspection.access_entry_id
                          ? "bg-green-500/20"
                          : isDarkMode
                            ? "bg-gray-700/40"
                            : "bg-gray-200/60"
                      }`}
                    >
                      <i
                        className={`ri-login-box-line text-lg ${
                          inspection.access_entry_id
                            ? "text-green-500"
                            : labelColor
                        }`}
                      ></i>
                    </div>
                    <div className="min-w-0">
                      {inspection.access_entry_id ? (
                        <>
                          <p className="text-sm font-medium text-primary-500">
                            Ver evento
                          </p>
                          <p
                            className={`text-[10px] font-mono ${themeClasses.textSecondary} truncate`}
                          >
                            ID: {inspection.access_entry_id}
                          </p>
                        </>
                      ) : (
                        <p className={`text-sm ${themeClasses.textSecondary}`}>
                          Sin evento ligado
                        </p>
                      )}
                    </div>
                  </div>
                </button>
                <div className="rounded-lg -m-1 p-1">
                  <p
                    className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium mb-1 flex items-center justify-between gap-1`}
                  >
                    <span className="flex items-center gap-1">
                      Salida
                      {inspection.access_exit_id && (
                        <i className="ri-arrow-right-up-line text-[10px]"></i>
                      )}
                    </span>
                    {inspection.access_exit_id && (
                      <button
                        type="button"
                        onClick={() => setPickerOpen((v) => !v)}
                        className="text-[10px] uppercase tracking-wider font-medium text-primary-500 hover:text-primary-400 transition-colors"
                      >
                        {pickerOpen ? "Cancelar" : "Cambiar"}
                      </button>
                    )}
                  </p>
                  {inspection.access_exit_id ? (
                    <button
                      type="button"
                      onClick={() => openAccessEvent(inspection.access_exit_id)}
                      title="Ver evento de acceso"
                      className={`w-full text-left flex items-center gap-2 rounded-lg -m-0.5 p-0.5 cursor-pointer transition-colors ${themeClasses.buttonHover} hover:ring-1 hover:ring-primary-500/40`}
                    >
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-red-500/20">
                        <i className="ri-logout-box-line text-lg text-red-500"></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-primary-500">
                          Ver evento
                        </p>
                        <p
                          className={`text-[10px] font-mono ${themeClasses.textSecondary} truncate`}
                        >
                          ID: {inspection.access_exit_id}
                        </p>
                      </div>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPickerOpen((v) => !v)}
                      className={`w-full text-left flex items-center gap-2 rounded-lg -m-0.5 p-0.5 cursor-pointer transition-colors ${themeClasses.buttonHover} hover:ring-1 hover:ring-primary-500/40`}
                    >
                      <div
                        className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isDarkMode ? "bg-gray-700/40" : "bg-gray-200/60"
                        }`}
                      >
                        <i
                          className={`ri-add-line text-lg ${labelColor}`}
                        ></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-primary-500">
                          {pickerOpen ? "Cancelar" : "Vincular salida"}
                        </p>
                        <p
                          className={`text-[10px] ${themeClasses.textSecondary} truncate`}
                        >
                          {pickerOpen ? "Cierra el selector" : "Elige un evento existente"}
                        </p>
                      </div>
                    </button>
                  )}
                </div>
              </div>

              {pickerOpen && (
                <ExitEventPicker
                  manualByPlate={manualByPlate}
                  sameVehicleUnlinked={sameVehicleUnlinked}
                  loading={loadingCandidates}
                  error={candidatesError}
                  hasVehicleIdentifier={hasVehicleIdentifier}
                  linkingId={linkingId}
                  themeClasses={themeClasses}
                  isDarkMode={isDarkMode}
                  surface={surface}
                  labelColor={labelColor}
                  onLink={handleLinkExit}
                  onCancel={() => setPickerOpen(false)}
                  // Con salida ya ligada (el panel también se abre desde "Cambiar")
                  // no hay salida manual que dar: sería llenar el formulario para un 409.
                  onManualExit={
                    onManualExit && !inspection.access_exit_id
                      ? () => setManualExitFormOpen((v) => !v)
                      : undefined
                  }
                  manualExitBusy={manualExitBusy}
                  manualExitBlockers={manualExitBlockers}
                  manualExitForm={
                    manualExitFormOpen && !manualExitBlocked ? (
                      <ManualExitForm
                        mode={exitSurveyMode}
                        loading={entrySource === undefined}
                        survey={exitSurvey}
                        onChange={setExitSurvey}
                        busy={manualExitBusy}
                        onConfirm={confirmManualExit}
                        onCancel={() => setManualExitFormOpen(false)}
                        themeClasses={themeClasses}
                        isDarkMode={isDarkMode}
                      />
                    ) : null
                  }
                />
              )}
            </div>

            {/* ── GPS: el evento de geocerca detrás de los pasos 1 y 4 ── */}
            <div
              className={`pt-3 border-t ${themeClasses.cardBorder} space-y-3`}
            >
              <p
                className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium`}
              >
                <i aria-hidden="true" className="ri-map-pin-2-line mr-1"></i>
                Geocerca / Tracking · GPS
              </p>
              {geofenceLoading ? (
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  Buscando evento de geocerca…
                </p>
              ) : geofenceEvent ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap text-[11px]">
                    {/* Decía "Patio:" con el nombre de la GEOCERCA. Pasaba
                        por bueno mientras el patio tenía su propia sección
                        al lado; con el patio en el título, dos "patios" con
                        nombres que pueden no coincidir era una contradicción
                        a la vista. */}
                    {geofenceEvent.geofence_name && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md font-medium ${
                          isDarkMode
                            ? "bg-teal-500/20 text-teal-300 border border-teal-500/30"
                            : "bg-teal-50 text-teal-700 border border-teal-200"
                        }`}
                      >
                        <i className="ri-map-pin-2-line mr-1 text-[10px]"></i>
                        Geocerca:
                        <span className="font-semibold ml-1">
                          {geofenceEvent.geofence_name}
                        </span>
                      </span>
                    )}
                    {geofenceEvent.duration != null && (
                      <span className={`${themeClasses.textSecondary}`}>
                        <i className="ri-time-line mr-0.5"></i>
                        Permanencia: {Math.round(geofenceEvent.duration / 60)} min
                      </span>
                    )}
                    {/* Entró y no hay cruce de salida: el recorrido dice "Sin
                        cruce" en el paso 4, y esto es lo que esa frase sola no
                        dice —que para el rastreador la unidad sigue adentro. */}
                    {geofenceEvent.entered_at && !geofenceEvent.exited_at && (
                      <span className="inline-flex items-center text-blue-500">
                        <i className="ri-focus-3-line mr-0.5"></i>
                        En geocerca
                      </span>
                    )}
                  </div>

                  {(geofenceEvent.enter_coordinates ||
                    (geofenceEvent.exited_at &&
                      geofenceEvent.exit_coordinates)) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                      {geofenceEvent.enter_coordinates && (
                        <p
                          className={`text-[11px] ${themeClasses.textSecondary}`}
                        >
                          <i className="ri-map-pin-add-line mr-1 text-green-500"></i>
                          Entrada:{" "}
                          <span className="font-mono">
                            {geofenceEvent.enter_coordinates.lat.toFixed(5)},{" "}
                            {geofenceEvent.enter_coordinates.lon.toFixed(5)}
                          </span>
                        </p>
                      )}
                      {geofenceEvent.exited_at &&
                        geofenceEvent.exit_coordinates && (
                          <p
                            className={`text-[11px] ${themeClasses.textSecondary}`}
                          >
                            <i className="ri-map-pin-2-fill mr-1 text-red-500"></i>
                            Salida:{" "}
                            <span className="font-mono">
                              {geofenceEvent.exit_coordinates.lat.toFixed(5)},{" "}
                              {geofenceEvent.exit_coordinates.lon.toFixed(5)}
                            </span>
                          </p>
                        )}
                    </div>
                  )}

                  <div className="pt-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <i
                        className={`ri-notification-3-line text-sm ${labelColor}`}
                      ></i>
                      <p
                        className={`text-[11px] uppercase tracking-wider ${labelColor} font-medium`}
                      >
                        Notificaciones enviadas:{" "}
                        <span className={themeClasses.text}>
                          {geofenceEvent.notifications.length}
                        </span>
                      </p>
                    </div>
                    {geofenceEvent.notifications.length === 0 ? (
                      <p className={`text-xs ${themeClasses.textSecondary}`}>
                        No se enviaron notificaciones en esta visita.
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {geofenceEvent.notifications.map((n, i) => {
                          const isExit = (n.title || "")
                            .toLowerCase()
                            .includes("salida");
                          return (
                            <div
                              key={i}
                              className={`rounded-lg p-2.5 ${isDarkMode ? "bg-[#1e1e1e]" : "bg-white"} border ${themeClasses.cardBorder}`}
                            >
                              {n.title && (
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold mb-1.5 ${
                                    isExit
                                      ? "bg-red-500/15 text-red-400"
                                      : "bg-green-500/15 text-green-400"
                                  }`}
                                >
                                  <i
                                    className={
                                      isExit
                                        ? "ri-logout-circle-line"
                                        : "ri-login-circle-line"
                                    }
                                  ></i>
                                  {n.title}
                                </span>
                              )}
                              <p
                                className={`text-xs leading-relaxed ${themeClasses.text}`}
                              >
                                {n.description || "Notificación enviada"}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  {geofenceReason === "vehicle_not_identified"
                    ? "Vehículo no identificado en tracking."
                    : geofenceReason === "yard_has_no_geofence"
                      ? "El patio no tiene geocerca asociada."
                      : geofenceReason === "error"
                        ? "No se pudo cargar el evento de geocerca."
                        : "Sin evento de geocerca para esta visita."}
                </p>
              )}
            </div>
          </CollapsibleSection>

          {/* Editable fields */}
          <div className="space-y-4">
            <p
              className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}
            >
              <i className="ri-edit-line mr-1"></i>Completar inspección
            </p>

            <div>
              <label
                className={`text-xs font-medium ${labelColor} mb-1.5 block`}
              >
                Vehículo
              </label>
              <Select
                options={vehicleOptions}
                value={selectedVehicleOption}
                onChange={(o) => handleVehicleChange(o as SelectOpt | null)}
                styles={selectStyles}
                placeholder={
                  hasAssignedVehicle
                    ? "Vehículo asignado"
                    : "Seleccionar vehículo..."
                }
                isClearable={!hasAssignedVehicle}
                isSearchable={!hasAssignedVehicle}
                isDisabled={hasAssignedVehicle}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : undefined
                }
                menuPosition="fixed"
              />
              {hasAssignedVehicle && (
                <p className={`mt-1 text-[11px] ${labelColor}`}>
                  Vehículo asignado a esta inspección.
                </p>
              )}
              {!vehicleId && (
                <div className="mt-2">
                  <label
                    className={`text-xs font-medium ${labelColor} mb-1 block`}
                  >
                    O ingresa la placa manualmente
                  </label>
                  <input
                    type="text"
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value)}
                    className={inputCls}
                    placeholder="Ej: ABC-123"
                  />
                </div>
              )}
            </div>

            <div>
              <label
                className={`text-xs font-medium ${labelColor} mb-1.5 block`}
              >
                Conductor
              </label>
              <input
                type="text"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                className={inputCls}
                placeholder="Nombre del conductor"
              />
            </div>

            {/* Un solo desplegable de agua. El tipo REPORTADO no se pregunta:
                se hereda del vehículo (ver el efecto de sincronización más
                arriba) y el inspector sólo declara lo que realmente extrajo.
                El reporte PDF sigue comparando ambos. */}
            <div>
              <label
                className={`text-xs font-medium ${labelColor} mb-1.5 block`}
              >
                Tipo de agua extraída
              </label>
              <Select
                options={WATER_TYPES}
                value={
                  WATER_TYPES.find((o) => o.value === waterType) || null
                }
                onChange={(o) =>
                  setWaterType((o as (typeof WATER_TYPES)[number] | null)?.value || "")
                }
                styles={selectStyles}
                placeholder="Seleccionar tipo de agua extraída..."
                isClearable
                isSearchable={false}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : undefined
                }
                menuPosition="fixed"
              />
            </div>

            <div>
              <label
                className={`text-xs font-medium ${labelColor} mb-1.5 block`}
              >
                Volumen extraído (L)
              </label>
              <input
                type="number"
                min={0}
                step="any"
                value={volumenExtraido}
                onChange={(e) => setVolumenExtraido(e.target.value)}
                placeholder="Ej. 8500"
                className={inputCls}
              />
            </div>

            {/* Título / Descripción / Notas: ocultos a petición del cliente —
                no forman parte de la inspección de descarga. Los campos siguen
                existiendo en el modelo y se envían intactos en handleSave, así
                que lo ya capturado no se pierde y otro tipo de inspección puede
                volver a mostrarlos sin tocar el backend. */}
          </div>

          {/* Evidences — 3 named slots (PH / Conductividad / Turbidez) */}
          <div className="space-y-3">
            <p
              className={`text-[11px] uppercase tracking-widest font-semibold ${labelColor}`}
            >
              <i className="ri-camera-line mr-1"></i>Fotografías de la
              inspección
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PHOTO_SLOTS.map((slot) => {
                const filled = findSlotEvidence(evidences, slot.label);
                const isUploading = uploadingSlot === slot.key;
                const warning = waterParameterWarning(
                  slot.key,
                  waterValues[slot.key],
                );
                return (
                  <div
                    key={slot.key}
                    className={`${surface} rounded-xl p-3 flex flex-col gap-2`}
                  >
                    <div className="flex items-center gap-2">
                      <i
                        className={`${slot.icon} text-primary-500 text-base`}
                      ></i>
                      <p
                        className={`text-sm font-semibold ${themeClasses.text}`}
                      >
                        {slot.label}
                      </p>
                      {/* La unidad, siempre a la vista. Es un SUPUESTO sin
                          confirmar con operación (ver WATER_PARAMETERS): si el
                          medidor de Acuapue entrega otra escala, el inspector
                          lo ve al capturar y avisa, en vez de que se mezclen
                          dos escalas en la misma columna y el promedio quede
                          sin significado y sin forma de separarlo después.
                          Va en el encabezado y no dentro del input para que se
                          lea antes de teclear y no compita con el placeholder
                          ni con el aviso de rango. */}
                      {slot.param.unit && (
                        <span
                          className={`ml-auto text-[10px] font-medium ${labelColor}`}
                        >
                          {slot.param.unit}
                        </span>
                      )}
                    </div>

                    {/* La lectura del medidor, pegada a su foto: el inspector
                        lee el instrumento una sola vez y deja la evidencia y el
                        dato juntos. Va antes de la foto y fuera del `filled ?`
                        para que se pueda escribir aunque la foto todavía no
                        esté, y para que se siga viendo cuando ya está. */}
                    <div>
                      {/* type="text", NO type="number", y el porqué importa:
                          un <input type="number"> reporta e.target.value === ""
                          para todo contenido que no sea un float válido, y la
                          coma decimal —el separador de es-MX, el que declara
                          válido el propio waterParameterWarning— se teclea sin
                          problema en Firefox, Safari y los teclados de Android
                          que dispara inputMode="decimal". El inspector veía
                          "7,5" en pantalla con el estado en "": sobre una
                          inspección que ya tenía ph "7.2", hasChanges evaluaba
                          "" !== "7.2", aparecía "Guardar cambios" y handleSave
                          mandaba ph: null. Se borraba una lectura guardada, sin
                          aviso, y la rama "No parece un número." del validador
                          era inalcanzable desde la UI.
                          De paso cierra la rueda del mouse: con type="number"
                          y el campo enfocado, Chrome y Firefox incrementan el
                          valor al scrollear. Bajar la rueda para llegar al
                          botón de guardar con el puntero sobre la caja de pH
                          convertía un 7.2 en 8.2 en silencio, dentro de 0-14 y
                          por lo tanto sin aviso de rango.
                          Validar es trabajo de waterParameterWarning, que es
                          además lo que exige guardar string libre. */}
                      <input
                        type="text"
                        inputMode="decimal"
                        value={waterValues[slot.key]}
                        onChange={(e) =>
                          setWaterValues((prev) => ({
                            ...prev,
                            [slot.key]: e.target.value,
                          }))
                        }
                        placeholder={slot.param.placeholder}
                        aria-label={`${slot.param.label}${slot.param.unit ? ` (${slot.param.unit})` : ""}`}
                        className={`w-full px-2.5 py-1.5 text-xs rounded-lg border ${warning ? "border-yellow-500/60" : themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card text-white placeholder-gray-500" : "bg-white text-gray-900 placeholder-gray-400"} focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
                      />
                      {/* Aviso, no bloqueo: un pH de 47 es casi siempre un
                          dedazo, pero rechazarlo dejaría la inspección sin
                          cerrar, que es peor. El valor se guarda igual. */}
                      {warning && (
                        <p className="mt-1 text-[10px] leading-snug text-yellow-500">
                          <i className="ri-error-warning-line mr-0.5"></i>
                          {warning}
                        </p>
                      )}
                    </div>

                    {filled ? (
                      <>
                        <div className="relative group rounded-lg overflow-hidden border border-transparent">
                          <button
                            type="button"
                            onClick={() => {
                              const idx = evidences.findIndex(
                                (e) => e.id === filled.id,
                              );
                              if (idx >= 0) setCarouselIndex(idx);
                            }}
                            className="block w-full aspect-square"
                          >
                            <img
                              src={filled.imageUrl}
                              alt={filled.title || slot.label}
                              className="w-full h-full object-cover"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeletePhoto(filled.id)}
                            className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Eliminar foto"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            value={getEvidenceDescriptionValue(filled)}
                            onChange={(e) =>
                              setEvidenceDescriptionDrafts((prev) => ({
                                ...prev,
                                [filled.id]: e.target.value,
                              }))
                            }
                            onBlur={() =>
                              handleEvidenceDescriptionBlur(filled)
                            }
                            disabled={
                              !onUpdateEvidence ||
                              savingEvidenceId === filled.id
                            }
                            placeholder="Descripción (opcional)"
                            className={`w-full px-2.5 py-1.5 pr-7 text-xs rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card text-white placeholder-gray-500" : "bg-white text-gray-900 placeholder-gray-400"} focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:opacity-60`}
                          />
                          {savingEvidenceId === filled.id && (
                            <i className="ri-loader-4-line animate-spin text-primary-500 absolute right-2 top-1/2 -translate-y-1/2 text-xs"></i>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={slotDescriptions[slot.key]}
                          onChange={(e) =>
                            setSlotDescriptions((prev) => ({
                              ...prev,
                              [slot.key]: e.target.value,
                            }))
                          }
                          placeholder="Descripción (opcional)"
                          className={`w-full px-2.5 py-1.5 text-xs rounded-lg border ${themeClasses.cardBorder} ${isDarkMode ? "bg-dark-card text-white placeholder-gray-500" : "bg-white text-gray-900 placeholder-gray-400"} focus:outline-none focus:ring-2 focus:ring-primary-500/30`}
                        />
                        {/* Sólo cámara: la evidencia tiene que ser tomada en el patio, en el
                            momento, y no una imagen cualquiera de la galería del teléfono. */}
                        <button
                          type="button"
                          onClick={() =>
                            setCameraTarget({
                              key: slot.key,
                              label: slot.label,
                            })
                          }
                          disabled={isUploading}
                          className="w-full text-xs px-2 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60 bg-green-600 hover:bg-green-700 text-white"
                        >
                          {isUploading ? (
                            <i className="ri-loader-4-line animate-spin"></i>
                          ) : (
                            <i className="ri-camera-line"></i>
                          )}
                          Tomar foto
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Extras — any uploaded evidence whose title doesn't match a slot */}
            {(() => {
              const extras = evidences.filter(isExtraEvidence);
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className={`text-[11px] ${labelColor}`}>
                      Otras fotografías {extras.length > 0 && `(${extras.length})`}
                    </p>
                    <button
                      type="button"
                      onClick={() => setCameraTarget("__extra__")}
                      disabled={uploadingSlot === "__extra__"}
                      className="text-[11px] px-2.5 py-1 rounded-lg font-semibold flex items-center gap-1 transition-colors disabled:opacity-60 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {uploadingSlot === "__extra__" ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-camera-line"></i>
                      )}
                      Tomar foto
                    </button>
                  </div>
                  {extras.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {extras.map((ev) => {
                        const idx = evidences.findIndex((e) => e.id === ev.id);
                        return (
                          <div
                            key={ev.id}
                            className={`relative group aspect-square rounded-lg overflow-hidden border ${themeClasses.cardBorder}`}
                          >
                            <button
                              type="button"
                              onClick={() => setCarouselIndex(idx)}
                              className="w-full h-full"
                            >
                              <img
                                src={ev.imageUrl}
                                alt={ev.title || `Foto ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePhoto(ev.id)}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              title="Eliminar foto"
                            >
                              <i className="ri-delete-bin-line text-xs"></i>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700/30 bg-inherit rounded-b-2xl">
          <button
            onClick={onClose}
            className={`px-5 py-2.5 rounded-xl ${themeClasses.buttonHover} ${themeClasses.buttonText} font-medium text-sm transition-all border ${themeClasses.cardBorder}`}
          >
            Cerrar
          </button>
          {hasChanges && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors disabled:opacity-60"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ri-save-line mr-1"></i>
                  Guardar cambios
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {carouselIndex !== null && evidences.length > 0 && (
        <PhotoCarouselModal
          evidences={evidences}
          initialIndex={carouselIndex}
          isDarkMode={isDarkMode}
          onClose={() => setCarouselIndex(null)}
        />
      )}

      {cameraTarget && (
        <CameraCaptureModal
          slotLabel={
            cameraTarget === "__extra__" ? "Otra fotografía" : cameraTarget.label
          }
          isDarkMode={isDarkMode}
          onCapture={handleCameraCapture}
          onClose={() => setCameraTarget(null)}
        />
      )}
    </div>
  );
}
